import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { Spring } from 'react-spring/renderprops-universal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';

import CompanyLogo from 'components/CompanyLogo';
import BasicControlGroup from 'components/BasicControlGroup';
import RatingCircleWidgetWithButtons from 'components/RatingCircleWidgetWithButtons';
import CommentIconButton from 'components/CommentIconButton';
import CommentsBlock from 'scenes/CommentsBlock';

import { ANIMATION_TIMEOUT_INOUT } from 'constants/animation';
import { LinkBuilder } from 'helpers/link-builder';
import { Role } from 'helpers/user';

import './styles.css';

const FirmPortfolioItem = props => {
  const {
    user,
    record: {
      PortfolioID,
      FirmName,
      FirmUrlAlias,
      Logo,
      Description,
      Transaction,
      Date,
      Type,
      Mark,
      MarkStatTotal,
      MarkStatGood,
      MarkStatBad,
      Status,
      Comments,
      CommentsCount,
      disabled = false,
      isFetchingComments = false,
    },
    onClickEdit,
    onClickDelete,
    onClickDealButton,
    onClickShowPreviousComments,
    onAddComment,
    onRemoveComment,
    editMode,
  } = props;

  const [isCommentsBlockOpen, setCommentsBlockOpen] = useState(false);

  const commentBlock = useRef(null);

  useEffect(() => {
    if (isCommentsBlockOpen) {
      commentBlock.current.focus();
    }
  }, [isCommentsBlockOpen]);

  const [markStatGood, markStatBad] = useMemo(
    () =>
      Number(MarkStatTotal) !== 0
        ? [
            Math.round((Number(MarkStatGood) / Number(MarkStatTotal)) * 100),
            Math.round((Number(MarkStatBad) / Number(MarkStatTotal)) * 100),
          ]
        : [100, 0],
    [MarkStatTotal, MarkStatGood, MarkStatBad]
  );

  const [firmLogo, firmLink] = useMemo(() => {
    const link =
      Status === 'Approved' ? LinkBuilder.getFirmLink(FirmUrlAlias) : null;

    return [
      <CompanyLogo
        name={FirmName}
        logo={Logo}
        link={link}
        styles={{
          width: '110px',
          height: '110px',
          minWidth: '110px',
          fontSize: '0.8571rem',
        }}
      />,
      link ? (
        <NavLink to={link} className="firm-portfolio-list__item-title-link">
          {FirmName}
        </NavLink>
      ) : (
        <span className="firm-portfolio-list__item-title">{FirmName}</span>
      ),
    ];
  }, [PortfolioID, Status, FirmName, FirmUrlAlias]);

  const showCommentsBlock = () => {
    if (Role.isAnonymous(user)) {
      window.location = '/signup';
    } else {
      setCommentsBlockOpen(isCommentsBlockOpen => !isCommentsBlockOpen);
    }
  };

  const handleClickEdit = () => {
    !disabled && onClickEdit(PortfolioID);
  };

  const handleClickDelete = () => {
    !disabled && onClickDelete({ PortfolioID, FirmName, Type });
  };

  const handleClickDealButton = mark => {
    !disabled && onClickDealButton({ PortfolioID, Type }, mark);
  };

  const handleAddComment = comment => {
    !isFetchingComments &&
      onAddComment({
        type: Type,
        portfolioId: PortfolioID,
        comment,
      });
  };

  const handleRemoveComment = id => {
    !isFetchingComments &&
      onRemoveComment({
        type: Type,
        portfolioId: PortfolioID,
        commentId: id,
      });
  };

  const handleClickLoadCommentButton = () => {
    !isFetchingComments &&
      onClickShowPreviousComments({
        type: Type,
        portfolioId: PortfolioID,
        commentId: Comments.length ? Comments[0].ID : null,
      });
  };

  return (
    <Spring
      from={{ opacity: 0 }}
      to={{ opacity: 1 }}
      config={{ duration: ANIMATION_TIMEOUT_INOUT }}
    >
      {props => (
        <div className="firm-portfolio-list__item" style={props}>
          <div className="firm-portfolio-list__item-main">
            <div className="firm-portfolio-list__item-logo">{firmLogo}</div>
            <div className="firm-portfolio-list__item-content">
              {firmLink}
              <div className="firm-portfolio-list__item-sub-title">
                {Transaction && (
                  <>
                    <span className="firm-portfolio-list__item-sub-title-desc">
                      Transaction:
                    </span>{' '}
                    {Transaction}
                  </>
                )}
              </div>
              <p className="firm-portfolio-list__item-text">{Description}</p>
            </div>
            <div className="firm-portfolio-list__item-date">
              {editMode && (
                <BasicControlGroup
                  groupStyles={{ marginRight: '0', justifyContent: 'flex-end' }}
                >
                  <div
                    className="firm-strategy-fund-item__control"
                    style={{
                      marginRight: 0,
                    }}
                    onClick={handleClickEdit}
                  >
                    <FontAwesomeIcon icon={faEdit} size="1x" />
                  </div>

                  <div
                    className="firm-strategy-fund-item__control"
                    onClick={handleClickDelete}
                    style={{
                      marginRight: 0,
                    }}
                  >
                    <FontAwesomeIcon icon={faTrash} size="1x" />
                  </div>
                </BasicControlGroup>
              )}
              {Date}
            </div>
          </div>

          <div className="firm-portfolio-list__item-rating">
            <div className="firm-portfolio-list__item-rating-widget">
              <RatingCircleWidgetWithButtons
                value={markStatGood}
                calcPercentage={false}
                maxValue={Number(MarkStatTotal)}
                basedOn="reviews"
                basedOnNumber={Number(MarkStatTotal)}
                color="#13a03a"
                icon="like"
                strokeWidth={6}
                onChange={handleClickDealButton}
                disabled={disabled}
                mark={Mark}
                showButtons={
                  typeof onClickDealButton === 'function' &&
                  Status === 'Approved'
                }
              />
            </div>

            <div className="firm-portfolio-list__item-rating-widget">
              <RatingCircleWidgetWithButtons
                value={markStatBad}
                calcPercentage={false}
                maxValue={Number(MarkStatTotal)}
                basedOn="reviews"
                basedOnNumber={Number(MarkStatTotal)}
                color="#cc0000"
                icon="dislike"
                strokeWidth={6}
                onChange={handleClickDealButton}
                disabled={disabled}
                mark={Mark}
                showButtons={
                  typeof onClickDealButton === 'function' &&
                  Status === 'Approved'
                }
              />
            </div>
          </div>

          {Status === 'Approved' && (
            <div className="firm-portfolio-list__footer">
              <div className="firm-portfolio-list__comment-icon">
                <CommentIconButton
                  count={CommentsCount}
                  onClick={showCommentsBlock}
                />
              </div>
            </div>
          )}

          {Status === 'Approved' && isCommentsBlockOpen && (
            <CommentsBlock
              ref={commentBlock}
              user={user}
              comments={Comments}
              count={CommentsCount}
              isFetching={isFetchingComments}
              onSendComment={handleAddComment}
              onRemove={handleRemoveComment}
              onClickLoadButton={handleClickLoadCommentButton}
            />
          )}
        </div>
      )}
    </Spring>
  );
};

export default FirmPortfolioItem;
